import { useState, useRef, useEffect } from 'react';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

export default function SmartCompanionPage() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: '你好呀！我是你的AI小伙伴，你可以问我任何问题，或者和我聊天玩耍！😊',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const topics = [
    { id: 'science', name: '科学知识', icon: 'ri-flask-line', color: 'blue' },
    { id: 'math', name: '数学趣题', icon: 'ri-calculator-line', color: 'green' },
    { id: 'story', name: '讲故事', icon: 'ri-book-open-line', color: 'purple' },
    { id: 'riddle', name: '猜谜语', icon: 'ri-question-line', color: 'orange' },
    { id: 'poem', name: '古诗词', icon: 'ri-quill-pen-line', color: 'pink' },
    { id: 'game', name: '小游戏', icon: 'ri-gamepad-line', color: 'indigo' }
  ];

  const quickQuestions = [
    '为什么天空是蓝色的？',
    '恐龙为什么会灭绝？',
    '给我讲个有趣的故事',
    '教我一首古诗',
    '出个谜语让我猜',
    '我们来玩成语接龙吧'
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (!inputText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputText,
      timestamp: new Date()
    };

    setMessages([...messages, userMessage]);
    setInputText('');
    setIsTyping(true);

    // 模拟AI回复
    setTimeout(() => {
      const aiResponses: Record<string, string> = {
        '为什么天空是蓝色的？': '这是个很棒的问题！天空是蓝色的，是因为阳光穿过大气层时，蓝色的光波比较短，更容易被空气中的小颗粒散射开来。所以我们抬头看天空时，就看到了蓝色！🌤️',
        '恐龙为什么会灭绝？': '科学家们认为，大约6500万年前，一颗巨大的小行星撞击了地球，引起了剧烈的气候变化，恐龙们无法适应这样的环境，就慢慢灭绝了。不过恐龙的后代——鸟类，现在还生活在我们身边呢！🦕',
        '给我讲个有趣的故事': '好的！从前有一只小兔子，它特别喜欢画画。有一天，它画了一扇门，没想到这扇门竟然真的可以打开！门后面是一个神奇的世界，里面住着会说话的彩虹和会唱歌的云朵...你想知道后来发生了什么吗？🌈',
        '教我一首古诗': '好的！我教你一首《咏鹅》：\n鹅，鹅，鹅，\n曲项向天歌。\n白毛浮绿水，\n红掌拨清波。\n这首诗描写了大白鹅在水中游泳的样子，是不是很生动呢？🦢',
        '出个谜语让我猜': '好的，听好了：\n"千条线，万条线，落到水里看不见。"\n猜猜这是什么？提示：下雨天会有哦！🌧️',
        '我们来玩成语接龙吧': '太好了！我先来：一心一意。\n现在轮到你了，要用"意"字开头的成语哦！加油！💪'
      };

      const response = aiResponses[inputText] || `这是个很有趣的问题！"${inputText}"。让我想想...作为你的AI小伙伴，我建议你可以多观察、多思考，也可以问问老师和家长哦！如果你还有其他问题，随时问我！😊`;

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const handleQuickQuestion = (question: string) => {
    setInputText(question);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/primary')}
                className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl text-gray-700"></i>
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <i className="ri-robot-line text-2xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">智能对话小伴侣</h1>
                  <p className="text-sm text-gray-600">和AI聊天，学习新知识</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Topics */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-bookmark-line mr-2 text-blue-600"></i>
                话题分类
              </h3>
              <div className="space-y-2">
                {topics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => setSelectedTopic(topic.id)}
                    className={`w-full px-4 py-3 rounded-lg text-left transition-all cursor-pointer ${
                      selectedTopic === topic.id
                        ? `bg-${topic.color}-100 text-${topic.color}-700 border-2 border-${topic.color}-300`
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <i className={`${topic.icon} mr-2`}></i>
                    {topic.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Questions */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-chat-smile-3-line mr-2 text-purple-600"></i>
                快速提问
              </h3>
              <div className="space-y-2">
                {quickQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickQuestion(question)}
                    className="w-full px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm hover:bg-purple-100 transition-all text-left cursor-pointer"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex items-start space-x-3 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.type === 'user' ? 'bg-pink-100' : 'bg-blue-100'
                      }`}>
                        <i className={`${message.type === 'user' ? 'ri-user-smile-line text-pink-600' : 'ri-robot-line text-blue-600'} text-xl`}></i>
                      </div>
                      <div>
                        <div className={`px-4 py-3 rounded-2xl ${
                          message.type === 'user'
                            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}>
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        </div>
                        <div className={`text-xs text-gray-400 mt-1 ${message.type === 'user' ? 'text-right' : 'text-left'}`}>
                          {message.timestamp.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-robot-line text-blue-600 text-xl"></i>
                      </div>
                      <div className="bg-gray-100 px-4 py-3 rounded-2xl">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="输入你想说的话..."
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-sm"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputText.trim()}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-send-plane-fill"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
